import { groupBy, handleApiError, IOmniaShell, memoize, msg, nullOrUndefinedFirstCompareFn, numericTextSort, react, utils } from "../omnia"
import { IFieldsService } from "../types"
import { FieldVm, FieldYearVm } from "./vm"

import clsx = react.classes;
import IMapTopologyService = omnia.map.IMapTopologyService;
import FormGroupTextInput = omnia.react.FormGroupTextInput;
import FormGroupSelect = omnia.react.FormGroupSelect;
import Msg = omnia.react.Msg;

/**
 * Defines the `DisableEnableFieldsDialog` React component to integrate with Angular
 */
export function defineDisableEnableFieldsDialog() {
    return react.defineReactComponent({
        inject: {
            shell: "OmniaShell",
            fieldSvc: "OmniaFieldsService",
            topo: "OmniaMapTopologyService",
        },

        start(context) {
            const { rootElement } = context;

            const element = React.createElement(DisableEnableFieldsDialog, {
                ...context,
            });

            ReactDOM.render(element, rootElement);
        },
    });
}

type State = {
    fields: FieldVm[];
    years: number[];
    firstYear: number;
};

/**
 * Dialog to disable/enable fields.
 */
function DisableEnableFieldsDialog({ dependencies, close }: react.ReactAngularComponentContext) {
    const shell: IOmniaShell = dependencies["shell"];
    const fieldSvc: IFieldsService = dependencies["fieldSvc"];
    const topo: IMapTopologyService = dependencies["topo"];

    const [{ fields, years, firstYear }, setState] = React.useState<State>(() => {
        const years = fieldSvc.holding.CropYears.map((x) => x.YearId);
        const fields = numericTextSort(FieldVm.create(fieldSvc.getFields(true), years, topo), (f) => f.name);
        const firstYear = years.length >= 5 ? years[years.length - 5] : years[0];
        return { fields, years, firstYear };
    });

    const forceRefreshState = React.useCallback(
        () =>
            setState((prev) => ({
                ...prev,
                years: [...prev.years],
                fields: [...prev.fields],
            })),
        []
    );

    const isReadOnly = React.useMemo(() => shell.isReadOnly((p) => p.fields), [shell]);

    const [fieldNameQuery, setFieldNameQuery] = React.useState<string>();

    const visibleYears = React.useMemo(() => {
        const fieldYearsByYear = groupBy(
            fields.flatMap((f) => f.years),
            (x) => x.year
        );
        return years
            .filter((x) => x >= firstYear)
            .map((y) => {
                const fieldYears = fieldYearsByYear.find((x) => x.key === y)?.values ?? [];
                return {
                    year: y,
                    fieldYears,
                };
            });
    }, [fields, firstYear, years]);

    const [fieldsFilter, setFieldsFilter] = React.useState<"all" | "unused">("all");

    const hasChanges = React.useMemo(() => fields.some((f) => f.hasChanged()), [fields]);

    const visibleFields = React.useMemo(() => {
        const fieldNameRegExp =
            fieldNameQuery && fieldNameQuery.length
                ? new RegExp(utils.regexp.escape(fieldNameQuery), "i")
                : undefined;

        return fields.filter((field) => {
            if (field.hasChanged()) {
                return true;
            }

            if (fieldsFilter === "unused" && !field.hasEmptyYears()) {
                return false;
            }

            if (
                fieldNameRegExp &&
                !(fieldNameRegExp.test(field.description) || fieldNameRegExp.test(field.field.fieldGroup))
            ) {
                return false;
            }

            return true;
        });
    }, [fields, fieldsFilter, fieldNameQuery]);

    const visibleGroups = React.useMemo(
        () =>
            numericTextSort(
                groupBy(visibleFields, (x) => x.field.fieldGroup),
                (x) => x.key,
                nullOrUndefinedFirstCompareFn
            ),
        [visibleFields]
    );

    const toggleFieldBoundary = React.useCallback(
        (field: FieldVm) => {
            if (!isReadOnly) {
                field.toggleBoundary();
                forceRefreshState();
            }
        },
        [isReadOnly, forceRefreshState]
    );

    const toggleYearIsDisabled = React.useCallback(
        (year: number, target: boolean) => {
            if (!isReadOnly) {
                fields
                    .flatMap((field) => field.years)
                    .filter(
                        (fieldYear) => fieldYear.year === year && (fieldYear.disabled || fieldYear.canDisable())
                    )
                    .forEach((fieldYear) => fieldYear.toggleDisabled(target));
                forceRefreshState();
            }
        },
        [fields, forceRefreshState, isReadOnly]
    );

    const toggleFieldYearIsDisabled = React.useCallback(
        (year: FieldYearVm) => {
            if (!isReadOnly) {
                year.toggleDisabled();
                forceRefreshState();
            }
        },
        [isReadOnly, forceRefreshState]
    );

    const [committing, setCommitting] = React.useState<boolean>(false);

    const commit = React.useCallback(() => {
        setCommitting(true);

        const model = fields
            .filter((field) => field.hasChanged())
            .map((field) => ({
                FieldId: field.field.fieldId,
                IgnoreBoundary: field.ignoreBoundary,
                DisabledYears: field.years.filter((y) => y.disabled).map((y) => y.year),
            }));

        fieldSvc
            .updateDisabledFields(model)
            .then(() => close(1))
            .catch((e) => {
                setCommitting(false);
                return handleApiError(shell, e);
            });
    }, [close, fieldSvc, fields, shell]);

    return (
        <div className="omnia-fields-disable-dialog">
            <omnia.react.DialogHeader icon="fas fa-remove">
                <Msg>Disable/enable fields</Msg>
            </omnia.react.DialogHeader>
            <omnia.react.DialogBody>
                <div className="hdr">
                    <div className="info">
                        <p>
                            <Msg>Use the checkboxes below to enable or disable fields for the cropping year.</Msg>
                        </p>
                    </div>
                    <div className="filters">
                        <FormGroupTextInput
                            placeholder={msg("Filter by field name/group")}
                            value={fieldNameQuery}
                            setValue={setFieldNameQuery}
                        />
                        <FormGroupSelect
                            options={fieldFilterOptions()}
                            value={fieldsFilter}
                            setValue={setFieldsFilter as never}
                        />
                        <FormGroupSelect
                            label="First year"
                            value={firstYear}
                            options={years.map((y) => [y, String(y)])}
                            setValue={(value) => setState((prev) => ({ ...prev, firstYear: value }))}
                            disabled={!years.length}
                        />
                    </div>
                </div>
                <div className="years">
                    {fields.length ? (
                        visibleFields.length ? (
                            <table className="table omnia-table">
                                <thead className="sticky">
                                    <tr>
                                        <th className="h"></th>
                                        <th className="hb">
                                            <div>
                                                <i className="fas fa-draw-polygon" />
                                                <Msg>Boundary</Msg>
                                                <omnia.react.InfoPopoverButton>
                                                    <p>
                                                        <Msg>
                                                            When enabled you will see a dotted outline of the field
                                                            in all years and will not be able to create another
                                                            field in the same place.
                                                        </Msg>
                                                    </p>
                                                    <p>
                                                        <Msg>
                                                            When disabled, the field will only be visible in years
                                                            that it is active, and other fields may be created in
                                                            the same place in all other years.
                                                        </Msg>
                                                    </p>
                                                </omnia.react.InfoPopoverButton>
                                            </div>
                                        </th>
                                        {visibleYears.map((x) => (
                                            <th key={x.year}>
                                                <omnia.react.Checkbox
                                                    checked={x.fieldYears.every((x) => !x.disabled)}
                                                    setChecked={(value) => toggleYearIsDisabled(x.year, !value)}
                                                    disabled={
                                                        fields.length != visibleFields.length ||
                                                        x.fieldYears.every((x) => x.crops)
                                                    }
                                                >
                                                    {x.year}
                                                </omnia.react.Checkbox>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                {visibleGroups.map(({ key = msg("(No field group)"), values: fields }) => (
                                    <tbody key={key}>
                                        <tr className="g">
                                            <td className="g">
                                                <span className="g-heading">
                                                    <img
                                                        src="/images/layers/folder-open.svg"
                                                        alt=""
                                                        height="18"
                                                        width="18"
                                                    />
                                                    <span className="g-heading-title">{key}</span>
                                                </span>
                                            </td>
                                            <td className="gb"></td>
                                            <td colSpan={visibleYears.length}></td>
                                        </tr>
                                        {fields.map((f: FieldVm) => (
                                            <tr key={f.id}>
                                                <td className="h">{f.description}</td>
                                                <td className="hb">
                                                    {f.canIgnoreBoundary() ? (
                                                        <omnia.react.Checkbox
                                                            disabled={!f.canIgnoreBoundary()}
                                                            checked={!f.ignoreBoundary}
                                                            title={f.getBoundaryTooltip()}
                                                            setChecked={() => toggleFieldBoundary(f)}
                                                        />
                                                    ) : null}
                                                </td>
                                                {f.years
                                                    .filter((y) => visibleYears.map((y) => y.year).includes(y.year))
                                                    .map((y) => (
                                                        <td
                                                            key={y.year}
                                                            className={clsx("y", {
                                                                empty: !y.crops,
                                                                disabled: y.disabled,
                                                                enabled: !y.disabled,
                                                            })}
                                                            onClick={
                                                                !y.crops
                                                                    ? () => toggleFieldYearIsDisabled(y)
                                                                    : undefined
                                                            }
                                                            title={
                                                                !y.crops
                                                                    ? msg(
                                                                        y.disabled
                                                                            ? "This field has been disabled in {{year}}. Click to enable."
                                                                            : "This field is active in {{year}}. Click to disable.",
                                                                        { year: y.year }
                                                                    )
                                                                    : undefined
                                                            }
                                                        >
                                                            {y.crops ? (
                                                                <omnia.react.CropCircles
                                                                    items={y.crops.map((c) => ({
                                                                        color: c.cssColor,
                                                                        title: c.name,
                                                                    }))}
                                                                />
                                                            ) : (
                                                                <omnia.react.Checkbox
                                                                    checked={!y.disabled}
                                                                    setChecked={() => toggleFieldYearIsDisabled(y)}
                                                                />
                                                            )}
                                                        </td>
                                                    ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                ))}
                            </table>
                        ) : (
                            <NoData>
                                <Msg>There are no fields which match the filters</Msg>
                            </NoData>
                        )
                    ) : (
                        <NoData>
                            <Msg>There are no fields to display.</Msg>
                        </NoData>
                    )}
                </div>
            </omnia.react.DialogBody>
            <omnia.react.DialogFooter>
                <omnia.react.DialogFooterButtons
                    onClose={close}
                    onCommit={commit}
                    hasChanges={hasChanges}
                    canCommit={!committing}
                    isReadOnly={isReadOnly}
                />
            </omnia.react.DialogFooter>
        </div>
    );
}

const NoData = ({ children = <Msg>No data to display</Msg> }: React.PropsWithChildren<object>) => (
    <div className="alert alert-info no-data-message">{children}</div>
);

const fieldFilterOptions: () => [value: "all" | "unused", text: string][] = memoize(() => ({
    provider: () => [
        ["all", msg("All fields")],
        ["unused", msg("Unused fields")],
    ],
    dependencies: []
}))
